"use client";

import React, { createContext, useContext, useMemo } from "react";

import {
  DEFAULT_TENANT,
  getTenantConfig,
  resolveTenantId,
  type TenantConfig,
} from "../config/tenants";

interface TenantContextType {
  /** Resolved tenant id (neuro, derma, …). */
  tenantId: string;
  /** Full config for the resolved tenant. */
  config: TenantConfig;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export function TenantProvider({
  tenantId,
  children,
}: {
  tenantId?: string;
  children: React.ReactNode;
}) {
  const value = useMemo<TenantContextType>(() => {
    const id = tenantId ?? resolveTenantId();
    return { tenantId: id, config: getTenantConfig(id) };
  }, [tenantId]);

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
}

export function useTenant(): TenantContextType {
  const context = useContext(TenantContext);
  if (!context) {
    // Resolve from the URL as a safe fallback if used outside the provider.
    const id = resolveTenantId() || DEFAULT_TENANT;
    return { tenantId: id, config: getTenantConfig(id) };
  }
  return context;
}
